 import React, { useState, useEffect, useRef } from 'react';
import SidebarTopbar from '../../components/layouts/layout';
import {
  Alert,
  MenuItem,
  Select,
  Button,
  IconButton,
} from '@mui/material';
import { RiEditLine } from 'react-icons/ri';
import DeleteIcon from '@mui/icons-material/Delete';
import './AccountSettings.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function AccountSettings() {
  const token = localStorage.getItem('authToken');

  const [alert, setAlert] = useState(null);
  useEffect(() => {
    if (!alert) return;
    const t = setTimeout(() => setAlert(null), 5000);
    return () => clearTimeout(t);
  }, [alert]);

 
  const [personal, setPersonal] = useState({ email:'', username:'', firstName:'', lastName:'' });
  const personalBackup = useRef(personal);
  const [editPersonal, setEditPersonal] = useState(false);
  const [personalError, setPersonalError] = useState('');
  useEffect(() => {
    if (!personalError) return;
    const t = setTimeout(() => setPersonalError(''), 5000);
    return () => clearTimeout(t);
  }, [personalError]);

  
  const [profilePic, setProfilePic] = useState('/default-profile.jpg');
  const [profileFile, setProfileFile] = useState(null);
  const profileBackup = useRef({ pic:profilePic, file:profileFile });
  const [editProfile, setEditProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  useEffect(() => {
    if (!profileError) return;
    const t = setTimeout(() => setProfileError(''), 5000);
    return () => clearTimeout(t);
  }, [profileError]);

 
  const [passwords, setPasswords] = useState({ oldPassword:'', newPassword:'', confirmPassword:'' });
  const passwordBackup = useRef(passwords);
  const [editPassword, setEditPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  useEffect(() => {
    if (!passwordError) return;
    const t = setTimeout(() => setPasswordError(''), 5000);
    return () => clearTimeout(t);
  }, [passwordError]);

 
  const therapyAreas = ['psoriasis arthritis','dermatology','rheumatology','other'];
  const [therapy, setTherapy] = useState('');
  const therapyBackup = useRef(therapy);
  const [editTherapy, setEditTherapy] = useState(false);
  const [therapyError, setTherapyError] = useState('');
  useEffect(() => {
    if (!therapyError) return;
    const t = setTimeout(() => setTherapyError(''), 5000);
    return () => clearTimeout(t);
  }, [therapyError]);

 
  const fetchAccountSettings = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BASE_URL}/getaccountsettings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      const data = await res.json();

      setPersonal({
        email: data.email,
        username: data.Username,
        firstName: data.firstName,
        lastName: data.lastName,
      });
      personalBackup.current = {
        email: data.email,
        username: data.Username,
        firstName: data.firstName,
        lastName: data.lastName,
      };

      setProfilePic(data.profile_picture || '/default-profile.jpg');
      profileBackup.current = {
        pic: data.profile_picture || '/default-profile.jpg',
        file: null,
      };

      setTherapy(data.therapy_area || '');
      therapyBackup.current = data.therapy_area || '';

    } catch {
      setAlert({ type:'error', msg:'Failed to load Account settings' });
    }
  };
  useEffect(() => {
    fetchAccountSettings();
  }, [token]);

 
  const resetSection = section => {
    switch (section) {
      case 'personal':
        setPersonal(personalBackup.current);
        setEditPersonal(false);
        setPersonalError('');
        break;
      case 'profile':
        setProfilePic(profileBackup.current.pic);
        setProfileFile(profileBackup.current.file);
        setEditProfile(false);
        setProfileError('');
        break;
      case 'password':
        setPasswords(passwordBackup.current);
        setEditPassword(false);
        setPasswordError('');
        break;
      case 'therapy':
        setTherapy(therapyBackup.current);
        setEditTherapy(false);
        setTherapyError('');
        break;
      default:
    }
  };

 
  const handleSave = async section => {
    setPersonalError(''); setProfileError(''); setPasswordError(''); setTherapyError('');
    const form = new FormData();
    let res, data;

    try {
      if (section === 'personal') {
        if (!personal.email || !personal.username)
          throw new Error('Email & Username required');
        form.append('email', personal.email);
        form.append('username', personal.username);
        form.append('first_name', personal.firstName);
        form.append('last_name', personal.lastName);
        res = await fetch(`${BASE_URL}/accountsettings`, {
          method:'POST',
          headers:{ Authorization:`Bearer ${token}` },
          body:form,
        });
        if (!res.ok) throw new Error('Failed updating personal details');
        data = await res.json();
        setPersonal({
          email:data.email,
          username:data.Username,
          firstName:data.firstName,
          lastName:data.lastName,
        });
        personalBackup.current = { ...personal };
        setEditPersonal(false);
      }

      else if (section === 'profile') {
        if (!profileFile) throw new Error('Please select a picture');
        form.append('profile_picture', profileFile);
        res = await fetch(`${BASE_URL}/accountsettings`, {
          method:'POST',
          headers:{ Authorization:`Bearer ${token}` },
          body:form,
        });
        if (!res.ok) throw new Error('Failed updating profile');
        data = await res.json();
        setProfilePic(data.image_url || '/default-profile.jpg');
        profileBackup.current = { pic:data.image_url, file:profileFile };
        setEditProfile(false);
      }

      else if (section === 'password') {
        if (!passwords.oldPassword) return setPasswordError('Old password required');
        if (!passwords.newPassword) return setPasswordError('New password required');
        if (passwords.newPassword !== passwords.confirmPassword)
          return setPasswordError('Passwords must match');
        form.append('old_password', passwords.oldPassword);
        form.append('new_password', passwords.newPassword);
        res = await fetch(`${BASE_URL}/accountsettings`, {
          method:'POST',
          headers:{ Authorization:`Bearer ${token}` },
          body:form,
        });
        if (res.status === 400) return setPasswordError('Incorrect old password');
        if (!res.ok) throw new Error('Failed updating password');
        setPasswords({ oldPassword:'', newPassword:'', confirmPassword:'' });
        setEditPassword(false);
      }

      else if (section === 'therapy') {
        form.append('therypay_area', therapy);
        res = await fetch(`${BASE_URL}/accountsettings`, {
          method:'POST',
          headers:{ Authorization:`Bearer ${token}` },
          body:form,
        });
        if (!res.ok) throw new Error('Failed updating therapy');
        therapyBackup.current = therapy;
        setEditTherapy(false);
      }

      setAlert({ type:'success', msg:'Updated successfully!' });
    } catch (err) {
      const msg = err.message || 'Error saving';
      switch (section) {
        case 'personal': setPersonalError(msg); break;
        case 'profile':  setProfileError(msg);  break;
        case 'password': setPasswordError(msg);  break;
        case 'therapy':  setTherapyError(msg);   break;
        default:         setAlert({ type:'error', msg });
      }
    }
  };

 
  const handleDeleteProfile = () => {
    setProfilePic('/default-profile.jpg');
    setProfileFile(null);
    profileBackup.current = { pic:'/default-profile.jpg', file:null };
    setEditProfile(false);
  };

  return (
    <SidebarTopbar>
      <div className="app-container">
        <main className="settings-content">
          <h2>Account Settings</h2>
          {alert && (
            <Alert
              severity={alert.type}
              onClose={() => setAlert(null)}
              className="settings-alert"
            >
              {alert.msg}
            </Alert>
          )}

 
          <section className="section">
            <div className="section-header">
              <h3 className="section-title">
                Personal Details
                <IconButton
                  size="small"
                  className="edit-btn-inline"
                  sx={{ color:'#1e4de3' }}
                  disabled={editPersonal}
                  onClick={() => {
                    personalBackup.current = personal;
                    setPersonalError('');
                    setEditPersonal(true);
                  }}
                >
                  <RiEditLine fontSize="small" />
                </IconButton>
              </h3>
              {editPersonal && personalError && (
                <Alert severity="error" className="inline-alert">
                  {personalError}
                </Alert>
              )}
            </div>
            <div className="section-body">
              {['email','username','firstName','lastName'].map(field => (
                <div className="field-group" key={field}>
                  <label>
                    {field==='firstName'?'First name':
                     field==='lastName'?'Last name':
                     field.charAt(0).toUpperCase()+field.slice(1)}:
                  </label>
                  <input
                    type={field==='email'?'email':'text'}
                    value={personal[field]}
                    disabled={!editPersonal}
                    onChange={e => setPersonal({...personal,[field]:e.target.value})}
                  />
                </div>
              ))}
            </div>
            {editPersonal && (
              <div className="section-actions">
                <Button className="primary-text" onClick={()=>resetSection('personal')}>Cancel</Button>
                <Button variant="contained" className="primary-bg" onClick={()=>handleSave('personal')}>Save</Button>
              </div>
            )}
          </section>

          {/* PROFILE PICTURE */}
          {/* <section className="section">
            <div className="section-header">
              <h3 className="section-title">
                Profile Picture
                <IconButton
                  size="small"
                  className="edit-btn-inline"
                  sx={{ color:'#1e4de3' }}
                  disabled={editProfile}
                  onClick={() => {
                    profileBackup.current = { pic:profilePic, file:profileFile };
                    setProfileError(''); setEditProfile(true);
                  }}
                >
                  <RiEditLine fontSize="small" />
                </IconButton>
              </h3>
              {editProfile && profileError && (
                <Alert severity="error" className="inline-alert">
                  {profileError}
                </Alert>
              )}
            </div>
            <div className="section-body">
              <img src={profilePic} alt="Profile" className="profile-img"/>
              {editProfile ? (
                <div className="profile-actions">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => {
                      setProfileFile(e.target.files[0]);
                      setProfilePic(URL.createObjectURL(e.target.files[0]));
                    }}
                  />
                  <Button className="primary-text" onClick={()=>resetSection('profile')}>Cancel</Button>
                  <Button variant="contained" className="primary-bg" onClick={()=>handleSave('profile')}>Save</Button>
                </div>
              ) : (
                <Button
                  sx={{ color:'#1e4de3' }}
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteProfile}
                >
                  Delete Picture
                </Button>
              )}
            </div>
          </section> */}

          {/* PASSWORD */}
          <section className="section">
            <div className="section-header">
              <h3 className="section-title">
                Password
                <IconButton
                  size="small"
                  className="edit-btn-inline"
                  sx={{ color:'#1e4de3' }}
                  disabled={editPassword}
                  onClick={() => {
                    passwordBackup.current = passwords;
                    setPasswords({ oldPassword:'', newPassword:'', confirmPassword:'' });
                    setPasswordError(''); setEditPassword(true);
                  }}
                >
                  <RiEditLine fontSize="small" />
                </IconButton>
              </h3>
              {editPassword && passwordError && (
                <Alert severity="error" className="inline-alert">
                  {passwordError}
                </Alert>
              )}
            </div>
            {editPassword && (
              <div className="section-body">
                {['oldPassword','newPassword','confirmPassword'].map(field=>(
                  <div className="field-group" key={field}>
                    <label>
                      {field==='oldPassword'?'Old password':
                       field==='newPassword'?'New password':'Confirm password'}:
                    </label>
                    <input
                      type="password"
                      value={passwords[field]}
                      onChange={e => setPasswords({...passwords,[field]:e.target.value})}
                    />
                  </div>
                ))}
                <div className="section-actions">
                  <Button className="primary-text" onClick={()=>resetSection('password')}>Cancel</Button>
                  <Button variant="contained" className="primary-bg" onClick={()=>handleSave('password')}>Save</Button>
                </div>
              </div>
            )}
          </section>

   
          <section className="section">
            <div className="section-header">
              <h3 className="section-title">
                Preferred Therapy Area
                <IconButton
                  size="small"
                  className="edit-btn-inline"
                  sx={{ color:'#1e4de3' }}
                  disabled={editTherapy}
                  onClick={() => {
                    therapyBackup.current = therapy;
                    setTherapyError(''); setEditTherapy(true);
                  }}
                >
                  <RiEditLine fontSize="small" />
                </IconButton>
              </h3>
              {editTherapy && therapyError && (
                <Alert severity="error" className="inline-alert">
                  {therapyError}
                </Alert>
              )}
            </div>
            <div className="section-body">
              <Select
                value={therapy}
                onChange={e => setTherapy(e.target.value)}
                disabled={!editTherapy}
                displayEmpty
                className="therapy-select"
              >
                <MenuItem value=""><em>Select therapy</em></MenuItem>
                {therapyAreas.map(a=><MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </div>
            {editTherapy && (
              <div className="section-actions">
                <Button className="primary-text" onClick={()=>resetSection('therapy')}>Cancel</Button>
                <Button variant="contained" className="primary-bg" onClick={()=>handleSave('therapy')}>Save</Button>
              </div>
            )}
          </section>

        </main>
      </div>
    </SidebarTopbar>
  );
}
